// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import constants from 'logic/data/Izenda.ReportDesignerConstants';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import reportsFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import ReportPartDetails from 'logic/models/Izenda.ReportPartDetails';
import { observable, action } from 'mobx';
import uiState from 'logic/Izenda.UiState.js';
import localizer from 'share/localization/Izenda.Localizer';
import embededReportUtils from 'share/utils/Izenda.EmbededReportUtils';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import IntergrateStore from 'share/utils/Izenda.IntergrateStore';
import izendaCfg from 'Izenda.Config';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ColorPaletteStore from 'logic/Izenda.ColorPaletteStore';
import {
  placeholder,
  find,
  flow,
  getOr,
  omit,
  get,
  trim,
  cloneDeep,
  toUpper,
  compact,
  concat,
  assign,
  unset,
  set,
  filter
} from 'lodash/fp';

export default class ReportPartStore {
  constructor() {
    this.draftId = null;
    this.requestForDraft = null;
    this.pageId = null;
  }

  @observable reportPart = null;
  @observable readyData = false;

  createReportPartModel(reportPartDefinition) {
    return new ReportPartDetails(reportPartDefinition);
  }

  /**
  * @method getReportPartData
  * @param {Array<Object} subReportFilter
  * @param {Boolean} isPrintMode
  * @param {Number} previewRecords user can change preview records number via API
  * @param {Object} newReportPart
  */
  getReportPartData(subReportFilter: Array<any>, isPrintMode: Boolean, previewRecords: Number, newReportPart: any) {
    return this.updatePreviewData(subReportFilter, newReportPart, null, null, isPrintMode, null, previewRecords).return(
      newReportPart
    );
  }

  /**
  * @method setReportPartModel
  * @param  {Object} newReportPart
  */
  @action('setReportPartModel')
  setReportPartModel(newReportPart: any) {
    this.reportPart = newReportPart;
    this.readyData = true;
    return newReportPart;
  }

  /**
   * this method receive report def json thus reduce request to get json object
   * @method updateDefinition
   * @param {Object} reportPartDef
   * @param {Array<Object>} subReportFilter
   * @param {Boolean} isPrintMode
   * @param {Number} previewRecords user can change preview records number via API
   * 
   */
  updateDefinition(reportPartDef: any, subReportFilter: Array<any>, isPrintMode: Boolean, previewRecords: Number) {
    // this method only modify newly created reportPart object
    // thus don't trigger re-render because this store dont' have reference to
    // this new report part object and reduce render times
    this.setReportPartModel(null);
    const newReportPart = this.createReportPartModel(cloneDeep(reportPartDef));

    return this.getReportPartData(subReportFilter, isPrintMode, previewRecords, newReportPart)
      .bind(this)
      .then(this.setReportPartModel);
  }

  /**
   * get draft report part definition
   * @method getDraftReportPartDefinition
   * @param {Number} draftId
   * @param {String} reportPartName
   * @param {Array<Object>} subReportFilter
   * @param {Boolean} isPrintMode
   * @param {Number} previewRecords user can change preview records number via API
   */

  getDraftReportPartDefinition(
    draftId: Number,
    reportPartName: String,
    subReportFilter: Array<any>,
    isPrintMode: Boolean,
    previewRecords: Number
  ) {
    // find correct report part, report part name in URL may be not in right format(uppper case etc)
    this.setReportPartModel(null);
    const formatTitle = flow(
      trim,
      toUpper
    );

    const reportPartNameFormated = formatTitle(reportPartName);

    return reportsFetcher
      .getPrintDraftData(draftId)
      .bind(this)
      .then(function(report) {
        const reportPart = find(function(reportPartDefinition) {
          return (
            reportPartDefinition.state !== ObjectStateConstants.DELETE &&
            formatTitle(reportPartDefinition.title) === reportPartNameFormated
          );
        }, report.reportPart);

        report.reportPart = compact([reportPart]);

        this.requestForDraft = {
          report: report,
          title: reportPart.title,
          reportKey: {
            key: report.id,
            tenantId: report.tenantId
          }
        };
        this.draftId = draftId;

        return this.createReportPartModel(reportPart);
      })
      .then(this.getReportPartData.bind(this, subReportFilter, isPrintMode, previewRecords))
      .then(this.setReportPartModel);
  }

  /**
   * this method receive report part id thus need call api request to get json object
   * @method getReportPartDefinition
   * @param {Number} id
   * @param {Array<Object>} subReportFilter
   * @param {Boolean} isPrintMode
   * @param {Number} reportId
   * @param {Number} previewRecords user can change preview records number via API
   */

  getReportPartDefinition(
    id: Number,
    subReportFilter: Array<any>,
    isPrintMode: Boolean,
    reportId: Number,
    previewRecords: Number
  ) {
    // this method only modify newly created reportPart object
    // thus don't trigger re-render because this store dont' have reference to
    // this new report part object and reduce render times
    this.setReportPartModel(null);
    return fetcher
      .loadReportPartDefinition(id, reportId, isPrintMode ? null : this.pageId)
      .bind(this)
      .then(this.createReportPartModel)
      .then(this.getReportPartData.bind(this, subReportFilter, isPrintMode, previewRecords))
      .then(this.setReportPartModel);
  }

  /**
   * update new preview dta for report part
   * @method updatePreviewData
   * @param {Array<Object>} subReportFilter
   * @param {Object} reportPart
   * @param {Object} drillDownData
   * @param {Number} pageIndex
   * @param {Boolean} isPrintMode
   * @param {Boolean} notUpdateFilter
   * @param {Number} previewRecords user can change preview records number via API
   */

  updatePreviewData(
    subReportFilter: Array<any>,
    reportPart: any,
    drillDownData: any,
    pageIndex: Number,
    isPrintMode: Boolean,
    notUpdateFilter: Boolean,
    previewRecords: Number
  ) {
    reportPart.startLoadData();
    const embededReportDefPromise = embededReportUtils.buildPromiseFetcher(
      reportPart,
      fetcher.loadReportPartDefinition.bind(fetcher),
      fetcher.loadForEdit.bind(fetcher),
      true,
      this.pageId
    );
    let drillInfo = reportPart.reportPartContent.getDrilldownInfo();

    if (!notUpdateFilter) {
      const filtersWithoutSeparator = filter(item => !item.isSeparator, drillInfo.filters);
      IntergrateStore.addFilters(filtersWithoutSeparator);
      IntergrateStore.cleanFilter();
    }

    let addingFitlers = getOr([], 'filters', drillDownData);

    if (izendaCfg.Integration && reportPart.reportPartContent.isCrossFiltering) {
      addingFitlers = ReportPartUtils.transformFilterWithReportPart(
        reportPart,
        getOr([], 'crossFilteringFilters', IntergrateStore)
      );
    }

    drillInfo.filters = concat(drillInfo.filters, addingFitlers);
    const colorPaletteStore = new ColorPaletteStore();

    return Promise.join(
      this.getPreviewData.apply(this, [subReportFilter, reportPart, drillInfo, pageIndex, isPrintMode, previewRecords]),
      embededReportDefPromise,
      colorPaletteStore.loadThemes(),
      action((result, embededReportDef, palettes) => {
        if (!result.messages) {
          reportPart.setQueryData(result);
          reportPart.readEmbededReportDef(embededReportDef);
          reportPart.colorPalettes = palettes;
        } else {
          reportPart.setQueryData([]);
        }
        return result.messages;
      })
    );
  }

  /**
   * get preview data match condition
   * @method getPreviewData
   * @param {Array<Object>} subReportFilter
   * @param {Object} reportPart
   * @param {Object} drillDownData
   * @param {Number} pageIndex
   * @param {Boolean} isPrintMode
   * @param {Number} previewRecords user can change preview records number via API
   * @return {promise}
   */

  getPreviewData(
    subReportFilter: Array<any>,
    reportPart: any,
    drillDownData: any,
    pageIndex: Number,
    isPrintMode: Boolean,
    previewRecords: Number
  ) {
    const { reportPartContent } = reportPart;
    const requestForDraft = this.requestForDraft;

    if (!reportPart.isBeingBuild) {
      return Promise.resolve([]);
    }
    // apply optional params based on report part content
    const applyOptionalParam = assign(reportPartContent.optionalParams);
    // apply params based on drill down information
    const applyDrillDownData = assign(placeholder, drillDownData);
    // apply page size to filters data receive
    const applyPageSize = queryData => {
      // if print mode then we set paging to load all.
      if (isPrintMode) {
        return unset('paging', queryData);
      }
      // pagination
      if (
        reportPartContent.usePagination ||
        reportPartContent.properties.usePagination ||
        get('properties.view.usePagination', reportPartContent)
      ) {
        return assign(queryData, {
          paging: {
            pageIndex: pageIndex || constants.DEFAULT_PAGE_INDEX,
            pageSize: reportPartContent.properties.pageSize || constants.DEFAULT_PAGE_SIZE
          }
        });
      }
      return queryData;
    };
    // merge current filtes with sub report filters
    const applySubReportFilter = queryData => {
      return flow(
        assign(placeholder, omit(['filters'], subReportFilter)),
        set('filters', compact(concat(queryData.filters, getOr([], 'filters', subReportFilter))))
      )(queryData);
    };

    const createBaseData = queryData => {
      if (!requestForDraft) {
        return assign(
          {
            reportId: reportPart.reportId,
            reportPartId: reportPart.id,
            loadDraft: false
          },
          queryData
        );
      }
      return flow(
        assign(requestForDraft),
        set('reportPart.report.reportPart', [reportPart.buildDataForSave()])
      )(queryData);
    };

    let data = {
      tenantId: uiState.getTenantId(),
      userAction: localizer.getText('REPORTDESIGNER_LOGGING_EMBEDDED_REPORT_PART'),
      previewRecords: previewRecords
    };

    data = flow(
      createBaseData,
      applyOptionalParam,
      applyPageSize,
      applyDrillDownData,
      applySubReportFilter
    )(data);

    if (isPrintMode) {
      data.loadAll = true;
    }

    const fetchFn = requestForDraft ? fetcher.getConfigReportPartPreviewData : fetcher.getPreviewData;

    return fetchFn(data).catch(function() {
      return [];
    });
  }
}


// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportPartStore.js